'use client';

import { useState, useCallback, useRef, useEffect } from 'react';
import { apiCall } from '@/app/messages/_lib/apiCall';

// Комментарий на русском: типы для голосового чата

export type VoiceQuality = 'auto' | 'low' | 'medium' | 'high';
export type ParticipantRole = 'host' | 'moderator' | 'participant';

export interface VoiceParticipant {
  user_id: number;
  display_name: string;
  avatar_url: string | null;
  role: ParticipantRole;
  muted_self: boolean;
  deafened_self: boolean;
  joined_at: string;
  speaking?: boolean; // Локальное состояние говорения
}

export interface VoiceCallState {
  active: boolean;
  call_id: number | null;
  started_by: { id: number; display_name: string; avatar_url: string | null } | null;
  started_at: string | null;
  e2ee_enabled: boolean;
  quality: VoiceQuality;
  participants: VoiceParticipant[];
}

export interface VoiceSettings {
  preferred_quality: VoiceQuality;
  auto_quality_enabled: boolean;
  preferred_mic_device_id: string | null;
  noise_suppression: boolean;
  echo_cancellation: boolean;
  auto_gain_control: boolean;
}

interface UseVoiceCallOptions {
  roomId: number;
  currentUserId?: number;
  onError?: (error: string) => void;
  onCallStarted?: (callId: number) => void;
  onCallEnded?: () => void;
}

const DEFAULT_VOICE_SETTINGS: VoiceSettings = {
  preferred_quality: 'auto',
  auto_quality_enabled: true,
  preferred_mic_device_id: null,
  noise_suppression: true,
  echo_cancellation: true,
  auto_gain_control: true,
};

const QUALITY_ORDER: Record<Exclude<VoiceQuality, 'auto'>, number> = {
  low: 0,
  medium: 1,
  high: 2,
};

const AUDIO_BITRATE_BPS: Record<Exclude<VoiceQuality, 'auto'>, number> = {
  low: 20000,
  medium: 32000,
  high: 48000,
};

function clampQualityToMax(q: Exclude<VoiceQuality, 'auto'>, max: Exclude<VoiceQuality, 'auto'>): Exclude<VoiceQuality, 'auto'> {
  return QUALITY_ORDER[q] > QUALITY_ORDER[max] ? max : q;
}

function stepDownQuality(q: Exclude<VoiceQuality, 'auto'>): Exclude<VoiceQuality, 'auto'> {
  if (q === 'high') return 'medium';
  if (q === 'medium') return 'low';
  return 'low';
}

function stepUpQuality(q: Exclude<VoiceQuality, 'auto'>): Exclude<VoiceQuality, 'auto'> {
  if (q === 'low') return 'medium';
  if (q === 'medium') return 'high';
  return 'high';
}

function inferQualityFromNetwork(): { quality: Exclude<VoiceQuality, 'auto'>; hint: string | null } {
  // Комментарий на русском: лёгкая эвристика на базе NetworkInformation API (пока без SFU/WebRTC stats).
  const c: any = typeof navigator !== 'undefined' ? (navigator as any).connection : null;
  const effectiveType = String(c?.effectiveType || '');
  const downlink = Number(c?.downlink || 0) || 0;
  const rtt = Number(c?.rtt || 0) || 0;

  const hintParts: string[] = [];
  if (effectiveType) hintParts.push(effectiveType);
  if (downlink) hintParts.push(`${downlink}Mbps`);
  if (rtt) hintParts.push(`${rtt}ms`);

  const hint = hintParts.length ? `сеть: ${hintParts.join(' · ')}` : null;

  if (effectiveType.includes('2g') || downlink > 0 && downlink < 0.7 || rtt > 800) return { quality: 'low', hint };
  if (effectiveType.includes('3g') || downlink > 0 && downlink < 1.5 || rtt > 400) return { quality: 'medium', hint };
  return { quality: 'high', hint };
}

type SfuInfo = { url: string; room_name: string; token: string };

type IceServer = { urls: string[]; username?: string; credential?: string };

type VoiceMode = 'sfu' | 'p2p';

type WebrtcStats = {
  loss: number | null; // 0..1
  jitterMs: number | null;
  rttMs: number | null;
  availableOutgoingBitrateKbps: number | null;
  hint: string | null;
};

function formatStatsHint(s: WebrtcStats): string | null {
  const parts: string[] = [];
  if (s.loss != null) parts.push(`loss ${Math.round(s.loss * 100)}%`);
  if (s.rttMs != null) parts.push(`rtt ${Math.round(s.rttMs)}ms`);
  if (s.jitterMs != null) parts.push(`jitter ${Math.round(s.jitterMs)}ms`);
  if (s.availableOutgoingBitrateKbps != null) parts.push(`up ${Math.round(s.availableOutgoingBitrateKbps)}kbps`);
  return parts.length ? parts.join(' · ') : null;
}

export function useVoiceCall({ roomId, currentUserId, onError, onCallStarted, onCallEnded }: UseVoiceCallOptions) {
  const [callState, setCallState] = useState<VoiceCallState>({
    active: false,
    call_id: null,
    started_by: null,
    started_at: null,
    e2ee_enabled: false,
    quality: 'auto',
    participants: [],
  });

  const [isConnecting, setIsConnecting] = useState(false);
  const [isMuted, setIsMuted] = useState(false);
  const [isDeafened, setIsDeafened] = useState(false);
  const [selectedMicId, setSelectedMicId] = useState<string | null>(null);
  const [availableMics, setAvailableMics] = useState<MediaDeviceInfo[]>([]);
  const [localStream, setLocalStream] = useState<MediaStream | null>(null);
  const [audioLevel, setAudioLevel] = useState(0);
  const [voiceSettings, setVoiceSettings] = useState<VoiceSettings>(DEFAULT_VOICE_SETTINGS);
  const voiceSettingsRef = useRef<VoiceSettings>(DEFAULT_VOICE_SETTINGS);
  const [effectiveQuality, setEffectiveQuality] = useState<Exclude<VoiceQuality, 'auto'>>('medium');
  const [qualityHint, setQualityHint] = useState<string | null>(null);
  const [webrtcStats, setWebrtcStats] = useState<WebrtcStats | null>(null);
  const effectiveQualityRef = useRef<Exclude<VoiceQuality, 'auto'>>('medium');
  const webrtcStatsRef = useRef<WebrtcStats | null>(null);

  const streamRef = useRef<MediaStream | null>(null);
  const audioContextRef = useRef<AudioContext | null>(null);
  const analyserRef = useRef<AnalyserNode | null>(null);
  const animationFrameRef = useRef<number>(0);
  const pollIntervalRef = useRef<NodeJS.Timeout | null>(null);
  const qualityIntervalRef = useRef<NodeJS.Timeout | null>(null);
  const statsIntervalRef = useRef<NodeJS.Timeout | null>(null);

  const lkRoomRef = useRef<any>(null);
  const lkLocalAudioTrackRef = useRef<any>(null);
  const lkAudioPubRef = useRef<any>(null);
  const lkAttachedAudioElsRef = useRef<HTMLMediaElement[]>([]);
  const lastSfuErrorRef = useRef<string | null>(null);
  const micStreamRef = useRef<MediaStream | null>(null);
  const micDeviceIdRef = useRef<string | null>(null);
  const isDeafenedRef = useRef(false);
  const isMutedRef = useRef(false);
  const stopLocalStreamRef = useRef<null | (() => void)>(null);
  const prevStatsRef = useRef<{
    packetsSent?: number;
    packetsLost?: number;
    ts?: number;
  }>({});
  const autoBadCountRef = useRef(0);
  const autoGoodMsRef = useRef(0);

  // ===== P2P mesh (fallback) =====
  // Комментарий на русском: лимит на число участников в P2P, иначе сеть/CPU клиента “взорвутся”.
  const P2P_MAX_PARTICIPANTS = 4;
  const p2pActiveRef = useRef(false);
  const p2pCallIdRef = useRef<number>(0);
  const p2pLastSignalIdRef = useRef<number>(0);
  const p2pPollTRef = useRef<any>(null);
  const p2pPollBusyRef = useRef(false);
  const p2pIceServersRef = useRef<IceServer[] | null>(null);
  const p2pAttachedAudioElsRef = useRef<Map<number, HTMLMediaElement>>(new Map());
  const p2pPeersRef = useRef<Map<number, {
    pc: RTCPeerConnection;
    pendingIce: RTCIceCandidateInit[];
    makingOffer: boolean;
    polite: boolean;
  }>>(new Map());

  // Комментарий на русском: единый API слой мессенджера (с Bearer + refresh).
  const apiJson = useCallback(async <T,>(endpoint: string, options: RequestInit = {}): Promise<T> => {
    const res = await apiCall(endpoint, options);
    const data = await res.json().catch(() => ({}));
    if (!res.ok) {
      throw new Error((data as any)?.error || `API Error: ${res.status}`);
    }
    return data as T;
  }, []);

  const lastReportedErrorRef = useRef<{ key: string; ts: number }>({ key: '', ts: 0 });
  const reportVoiceError = useCallback((message: string, details?: any) => {
    const msg = String(message || '').trim();
    if (!msg) return;

    // Комментарий на русском: локально показываем ошибку (как раньше).
    onError?.(msg);

    // Комментарий на русском: отправляем в “Сайт/ошибки” (frontend_errors), с дедупликацией.
    const now = Date.now();
    const key = `${roomId}|${callState.call_id || 0}|${msg}`;
    if (lastReportedErrorRef.current.key === key && now - lastReportedErrorRef.current.ts < 8000) return;
    lastReportedErrorRef.current = { key, ts: now };

    const payload = {
      errors: [
        {
          category: 'voice',
          message: msg,
          details: details ? JSON.stringify(details).slice(0, 60000) : null,
          url: typeof window !== 'undefined' ? window.location.href : null,
          userAgent: typeof navigator !== 'undefined' ? navigator.userAgent : null,
          stack: null,
        },
      ],
    };

    void apiCall('/api/errors/collect', {
      method: 'POST',
      body: JSON.stringify(payload),
    }).catch(() => {});
  }, [onError, roomId, callState.call_id]);

  useEffect(() => {
    voiceSettingsRef.current = voiceSettings;
  }, [voiceSettings]);

  const loadVoiceSettings = useCallback(async () => {
    try {
      const s = await apiJson<VoiceSettings>(`/api/chat/voice/settings/`);
      const next = { ...DEFAULT_VOICE_SETTINGS, ...(s || {}) };
      voiceSettingsRef.current = next;
      setVoiceSettings(next);
      if (next.preferred_mic_device_id) setSelectedMicId(next.preferred_mic_device_id);
      return next;
    } catch (e: any) {
      console.error('Failed to load voice settings:', e);
      return null;
    }
  }, [apiJson]);

  const saveVoiceSettings = useCallback(async (patch: Partial<VoiceSettings>) => {
    const next = { ...voiceSettingsRef.current, ...patch };
    voiceSettingsRef.current = next;
    setVoiceSettings(next);
    try {
      await apiJson(`/api/chat/voice/settings/`, {
        method: 'PATCH',
        body: JSON.stringify(next),
      });
      return true;
    } catch (e: any) {
      console.error('Failed to save voice settings:', e);
      reportVoiceError(e?.message || 'Не удалось сохранить настройки голоса', { scope: 'voice_settings_save' });
      return false;
    }
  }, [apiJson, reportVoiceError]);

  const computeEffectiveQuality = useCallback((maxQuality?: VoiceQuality) => {
    const maxQ = maxQuality ?? voiceSettingsRef.current.preferred_quality;
    const autoEnabled = maxQ === 'auto' || !!voiceSettingsRef.current.auto_quality_enabled;
    const maxCap: Exclude<VoiceQuality, 'auto'> = (maxQ === 'auto' ? 'high' : maxQ);
    const inferred = inferQualityFromNetwork();
    const eff: Exclude<VoiceQuality, 'auto'> = autoEnabled ? clampQualityToMax(inferred.quality, maxCap) : maxCap;
    return { effective: eff, hint: inferred.hint };
  }, []);

  useEffect(() => {
    isDeafenedRef.current = isDeafened;
    lkAttachedAudioElsRef.current.forEach((el) => {
      try {
        (el as any).muted = isDeafened;
        (el as any).volume = isDeafened ? 0 : 1;
      } catch {}
    });
  }, [isDeafened]);

  useEffect(() => {
    isMutedRef.current = isMuted;
  }, [isMuted]);

  useEffect(() => {
    effectiveQualityRef.current = effectiveQuality;
  }, [effectiveQuality]);

  useEffect(() => {
    webrtcStatsRef.current = webrtcStats;
  }, [webrtcStats]);

  // Получить список микрофонов
  const loadMicrophones = useCallback(async () => {
    try {
      const devices = await navigator.mediaDevices.enumerateDevices();
      const mics = devices.filter(d => d.kind === 'audioinput');
      setAvailableMics(mics);
      return mics;
    } catch (e) {
      console.error('Failed to load microphones:', e);
      return [];
    }
  }, []);

  // Получить состояние звонка
  const fetchState = useCallback(async () => {
    if (roomId <= 0) return;
    try {
      const state = await apiJson<VoiceCallState>(`/api/chat/rooms/${roomId}/voice-call/state/`);
      setCallState(state);
      // Комментарий на русском: синхронизируем mute/deafen по данным сервера для себя (важно при реконнекте/другом устройстве).
      const uid = Number(currentUserId || 0) || 0;
      if (uid && state?.active && Array.isArray(state.participants)) {
        const me = state.participants.find(p => Number(p.user_id) === uid);
        if (me) {
          setIsMuted(!!me.muted_self);
          setIsDeafened(!!me.deafened_self);
        }
      }
      return state;
    } catch (e) {
      console.error('Failed to fetch voice state:', e);
      return null;
    }
  }, [roomId, apiJson, currentUserId]);

  const stopSfu = useCallback(async () => {
    try {
      if (statsIntervalRef.current) clearInterval(statsIntervalRef.current);
      statsIntervalRef.current = null;
      prevStatsRef.current = {};
      setWebrtcStats(null);
    } catch {}

    try {
      lkAttachedAudioElsRef.current.forEach((el) => {
        try { el.remove(); } catch {}
      });
      lkAttachedAudioElsRef.current = [];
    } catch {}

    try {
      const room = lkRoomRef.current;
      lkRoomRef.current = null;
      lkAudioPubRef.current = null;
      const t = lkLocalAudioTrackRef.current;
      lkLocalAudioTrackRef.current = null;
      if (t?.stop) t.stop();
      if (room?.disconnect) room.disconnect();
    } catch {}
  }, []);

  const stopP2p = useCallback(async () => {
    try {
      p2pActiveRef.current = false;
      p2pCallIdRef.current = 0;
      p2pLastSignalIdRef.current = 0;
    } catch {}

    try {
      if (p2pPollTRef.current) clearInterval(p2pPollTRef.current);
      p2pPollTRef.current = null;
      p2pPollBusyRef.current = false;
    } catch {}

    try {
      p2pPeersRef.current.forEach((v) => {
        try { v.pc.ontrack = null as any; } catch {}
        try { v.pc.onicecandidate = null as any; } catch {}
        try { v.pc.onconnectionstatechange = null as any; } catch {}
        try { v.pc.close(); } catch {}
      });
      p2pPeersRef.current.clear();
    } catch {}

    try {
      p2pAttachedAudioElsRef.current.forEach((el) => {
        try { el.remove(); } catch {}
      });
      p2pAttachedAudioElsRef.current.clear();
    } catch {}
  }, []);

  const p2pSignalSend = useCallback(async (callId: number, toUserId: number, kind: 'offer' | 'answer' | 'ice', payload: any) => {
    if (!callId || !toUserId) return false;
    try {
      await apiJson(`/api/chat/rooms/${roomId}/voice-call/state/?action=voice_p2p_signal_send`, {
        method: 'POST',
        body: JSON.stringify({ call_id: callId, to_user_id: toUserId, kind, payload }),
      });
      return true;
    } catch {
      return false;
    }
  }, [apiJson, roomId]);

  const p2pHandleOffer = useCallback(async (fromUserId: number, offer: RTCSessionDescriptionInit) => {
    const me = Number(currentUserId || 0) || 0;
    const callId = p2pCallIdRef.current;
    if (!me || !callId || !offer?.sdp) return;

    const existing = p2pPeersRef.current.get(fromUserId) || null;
    const polite = me > fromUserId;
    const ensure = () => {
      const found = p2pPeersRef.current.get(fromUserId);
      if (found) return found;
      const iceServers = p2pIceServersRef.current || undefined;
      const pc = new RTCPeerConnection({ iceServers: (iceServers || []) as any });
      const pendingIce: RTCIceCandidateInit[] = [];
      const peer = { pc, pendingIce, makingOffer: false, polite };
      p2pPeersRef.current.set(fromUserId, peer);

      // Комментарий на русском: подключаем локальный микрофон к этому peer’у.
      try {
        const ms = micStreamRef.current;
        const t = ms?.getAudioTracks?.()?.[0] || null;
        if (ms && t) pc.addTrack(t, ms);
      } catch {}

      pc.onicecandidate = (ev) => {
        const c = ev?.candidate;
        if (!c) return;
        void p2pSignalSend(callId, fromUserId, 'ice', c.toJSON ? c.toJSON() : c);
      };

      pc.ontrack = (ev) => {
        try {
          const stream = (ev?.streams && ev.streams[0]) ? ev.streams[0] : new MediaStream([ev.track]);
          if (p2pAttachedAudioElsRef.current.has(fromUserId)) return;
          const el = document.createElement('audio');
          el.autoplay = true;
          el.muted = isDeafenedRef.current;
          el.volume = isDeafenedRef.current ? 0 : 1;
          el.style.display = 'none';
          el.srcObject = stream;
          document.body.appendChild(el);
          p2pAttachedAudioElsRef.current.set(fromUserId, el);
          try { (el as any).play?.().catch?.(() => {}); } catch {}
        } catch {}
      };

      return peer;
    };

    const peer = ensure();

    try {
      const pc = peer.pc;
      const offerCollision = peer.makingOffer || pc.signalingState !== 'stable';
      if (offerCollision && !peer.polite) return;
      if (offerCollision && peer.polite) {
        try { await pc.setLocalDescription({ type: 'rollback' } as any); } catch {}
      }

      await pc.setRemoteDescription(offer);
      for (const c of peer.pendingIce.splice(0)) {
        try { await pc.addIceCandidate(c as any); } catch {}
      }
      const answer = await pc.createAnswer();
      await pc.setLocalDescription(answer);
      void p2pSignalSend(callId, fromUserId, 'answer', pc.localDescription);
    } catch {}
  }, [currentUserId, p2pSignalSend]);

  const p2pHandleAnswer = useCallback(async (fromUserId: number, answer: RTCSessionDescriptionInit) => {
    const peer = p2pPeersRef.current.get(fromUserId);
    if (!peer || !answer?.sdp) return;
    try {
      await peer.pc.setRemoteDescription(answer);
      for (const c of peer.pendingIce.splice(0)) {
        try { await peer.pc.addIceCandidate(c as any); } catch {}
      }
    } catch {}
  }, []);

  const p2pHandleIce = useCallback(async (fromUserId: number, candidate: RTCIceCandidateInit) => {
    const peer = p2pPeersRef.current.get(fromUserId);
    if (!peer) {
      // Комментарий на русском: если кандидаты пришли раньше peer’а/offer’а — копим.
      p2pPeersRef.current.set(fromUserId, {
        pc: new RTCPeerConnection({ iceServers: (p2pIceServersRef.current || []) as any }),
        pendingIce: [candidate],
        makingOffer: false,
        polite: (Number(currentUserId || 0) || 0) > fromUserId,
      });
      // Комментарий на русском: этот “пустой” pc будет пересоздан/дозаполнен при offer; чтобы не усложнять — игнорим.
      try { p2pPeersRef.current.get(fromUserId)?.pc.close(); } catch {}
      p2pPeersRef.current.delete(fromUserId);
      return;
    }
    try {
      if (peer.pc.remoteDescription) {
        await peer.pc.addIceCandidate(candidate as any);
      } else {
        peer.pendingIce.push(candidate);
      }
    } catch {}
  }, [currentUserId]);

  const p2pPollSignals = useCallback(async () => {
    const callId = p2pCallIdRef.current;
    if (!p2pActiveRef.current || !callId) return;
    if (p2pPollBusyRef.current) return;
    p2pPollBusyRef.current = true;
    try {
      const since = p2pLastSignalIdRef.current || 0;
      const r = await apiJson<{
        ok: boolean;
        signals: Array<{ id: number; from_user_id: number; kind: string; payload: any }>;
        last_id: number;
        ended?: boolean;
      }>(`/api/chat/rooms/${roomId}/voice-call/state/?action=voice_p2p_signal_poll&call_id=${callId}&since_id=${since}`);
      if (typeof r?.last_id === 'number') p2pLastSignalIdRef.current = r.last_id;
      const arr = Array.isArray(r?.signals) ? r.signals : [];
      for (const s of arr) {
        const from = Number((s as any)?.from_user_id || 0) || 0;
        const kind = String((s as any)?.kind || '');
        const payload = (s as any)?.payload;
        if (!from || from === Number(currentUserId || 0)) continue;
        if (kind === 'offer') await p2pHandleOffer(from, payload as any);
        else if (kind === 'answer') await p2pHandleAnswer(from, payload as any);
        else if (kind === 'ice') await p2pHandleIce(from, payload as any);
      }
    } catch {
      // ignore
    } finally {
      p2pPollBusyRef.current = false;
    }
  }, [apiJson, roomId, currentUserId, p2pHandleOffer, p2pHandleAnswer, p2pHandleIce]);

  const p2pEnsureOfferToPeer = useCallback(async (peerUserId: number) => {
    const me = Number(currentUserId || 0) || 0;
    const callId = p2pCallIdRef.current;
    if (!me || !callId || !peerUserId || peerUserId === me) return;
    // Комментарий на русском: чтобы не было glare — offer делает только меньший user_id.
    if (me > peerUserId) return;

    let peer = p2pPeersRef.current.get(peerUserId) || null;
    if (!peer) {
      const iceServers = p2pIceServersRef.current || undefined;
      const pc = new RTCPeerConnection({ iceServers: (iceServers || []) as any });
      peer = { pc, pendingIce: [], makingOffer: false, polite: false };
      p2pPeersRef.current.set(peerUserId, peer);

      try {
        const ms = micStreamRef.current;
        const t = ms?.getAudioTracks?.()?.[0] || null;
        if (ms && t) pc.addTrack(t, ms);
      } catch {}

      pc.onicecandidate = (ev) => {
        const c = ev?.candidate;
        if (!c) return;
        void p2pSignalSend(callId, peerUserId, 'ice', c.toJSON ? c.toJSON() : c);
      };

      pc.ontrack = (ev) => {
        try {
          const stream = (ev?.streams && ev.streams[0]) ? ev.streams[0] : new MediaStream([ev.track]);
          if (p2pAttachedAudioElsRef.current.has(peerUserId)) return;
          const el = document.createElement('audio');
          el.autoplay = true;
          el.muted = isDeafenedRef.current;
          el.volume = isDeafenedRef.current ? 0 : 1;
          el.style.display = 'none';
          el.srcObject = stream;
          document.body.appendChild(el);
          p2pAttachedAudioElsRef.current.set(peerUserId, el);
          try { (el as any).play?.().catch?.(() => {}); } catch {}
        } catch {}
      };
    }

    try {
      peer.makingOffer = true;
      const offer = await peer.pc.createOffer();
      await peer.pc.setLocalDescription(offer);
      void p2pSignalSend(callId, peerUserId, 'offer', peer.pc.localDescription);
    } catch {
      // ignore
    } finally {
      if (peer) peer.makingOffer = false;
    }
  }, [currentUserId, p2pSignalSend]);

  const startP2p = useCallback(async (callId: number, iceServers?: IceServer[]) => {
    const me = Number(currentUserId || 0) || 0;
    if (!me) {
      reportVoiceError('P2P режим недоступен: не определён текущий пользователь', { scope: 'p2p_start_no_user' });
      return false;
    }
    p2pIceServersRef.current = Array.isArray(iceServers) ? iceServers : null;
    p2pCallIdRef.current = callId;
    p2pActiveRef.current = true;
    p2pLastSignalIdRef.current = 0;

    try {
      if (p2pPollTRef.current) clearInterval(p2pPollTRef.current);
      p2pPollTRef.current = setInterval(() => void p2pPollSignals(), 500);
    } catch {}

    // Комментарий на русском: первый poll сразу, чтобы быстро поймать offer’ы.
    void p2pPollSignals();
    return true;
  }, [currentUserId, reportVoiceError, p2pPollSignals]);

  const startAnalyserFromStream = useCallback((stream: MediaStream) => {
    // Комментарий на русском: используем один анализатор громкости и для LiveKit, и для fallback.
    streamRef.current = stream;
    setLocalStream(stream);
    const audioCtx = new AudioContext();
    const analyser = audioCtx.createAnalyser();
    const source = audioCtx.createMediaStreamSource(stream);
    source.connect(analyser);
    analyser.fftSize = 256;
    audioContextRef.current = audioCtx;
    analyserRef.current = analyser;
    const updateLevel = () => {
      if (!analyserRef.current) return;
      const data = new Uint8Array(analyserRef.current.frequencyBinCount);
      analyserRef.current.getByteFrequencyData(data);
      const avg = data.reduce((a, b) => a + b, 0) / data.length;
      setAudioLevel(avg / 255);
      animationFrameRef.current = requestAnimationFrame(updateLevel);
    };
    updateLevel();
  }, []);

  const startSfu = useCallback(async (
    sfu: SfuInfo,
    chosenMic: string | undefined,
    q: Exclude<VoiceQuality, 'auto'>,
    iceServers?: IceServer[],
    iceTransportPolicy?: 'all' | 'relay',
  ) => {
    // Комментарий на русском: подключаемся к LiveKit SFU по токену, публикуем микрофон.
    try {
      lastSfuErrorRef.current = null;
      await stopSfu();
        // Комментарий на русском: микрофон уже захвачен заранее (acquireMicrophone), поэтому не гасим локальный стрим здесь.

      const lk = await import('livekit-client');
      const { Room, RoomEvent, DisconnectReason, ParticipantEvent } = lk as any;

      const s = voiceSettingsRef.current;
      const room = new Room({
        publishDefaults: {
          audioBitrate: AUDIO_BITRATE_BPS[q],
        },
      });

      // Комментарий на русском: если нас принудительно отключили (владелец завершил звонок / kick),
      // то чистим локальное состояние, чтобы UI сразу перешёл в "нет звонка".
      room.on(RoomEvent.Disconnected, (reason: any) => {
        // Комментарий на русском: показываем причину отключения, иначе выглядит как "микрофон включился и выключился".
        try {
          const reasonName = (() => {
            if (typeof reason === 'string') return reason;
            if (DisconnectReason) {
              try {
                return (DisconnectReason[reason] || DisconnectReason[Number(reason)] || String(reason)) as string;
              } catch {}
            }
            return String(reason);
          })();
          if (reasonName.includes('DUPLICATE_IDENTITY')) onError?.('Вы уже подключены к голосовому чату в другом окне/устройстве');
          else if (reasonName.includes('PARTICIPANT_REMOVED')) onError?.('Вас исключили из голосового чата');
          else if (reasonName.includes('ROOM_DELETED') || reasonName.includes('ROOM_CLOSED')) onError?.('Голосовой чат завершён');
          else if (reasonName.includes('JOIN_FAILURE')) onError?.('Не удалось подключиться к голосовому чату (join failure)');
          else if (reasonName.includes('CONNECTION_TIMEOUT')) onError?.('Не удалось подключиться к голосовому чату (timeout)');
        } catch {}
        try { stopLocalStreamRef.current?.(); } catch {}
        void stopSfu();
        setCallState(prev => ({ ...prev, active: false, call_id: null, participants: [] }));
        setIsMuted(false);
        setIsDeafened(false);
      });

      // Комментарий на русском: если звук "публикуется", но реально тишина — LiveKit может это детектить.
      room.on(RoomEvent.LocalAudioSilenceDetected, () => {
        onError?.('Микрофон не передаёт звук. Проверьте выбранное устройство и разрешения.');
      });

      // Комментарий на русском: ошибки устройств (например, устройство занято/не удалось открыть).
      try {
        room.localParticipant?.on?.(ParticipantEvent.MediaDevicesError, (e: any) => {
          const name = String(e?.name || e?.error?.name || '');
          onError?.(name ? `Ошибка устройства (${name})` : 'Ошибка устройства аудио');
        });
      } catch {}

      room.on(RoomEvent.TrackSubscribed, (track: any) => {
        try {
          if (!track?.attach) return;
          const el = track.attach();
          el.autoplay = true;
          el.muted = isDeafenedRef.current;
          el.volume = isDeafenedRef.current ? 0 : 1;
          el.style.display = 'none';
          document.body.appendChild(el);
          lkAttachedAudioElsRef.current.push(el);
          // Комментарий на русском: некоторые браузеры блокируют autoplay, поэтому явно дергаем play().
          try {
            const p = (el as any).play?.();
            if (p?.catch) p.catch(() => {});
          } catch {}
        } catch {}
      });

      room.on(RoomEvent.TrackUnsubscribed, (track: any) => {
        try {
          const els = track?.detach?.() || [];
          (Array.isArray(els) ? els : [els]).forEach((el: any) => {
            try { el.remove(); } catch {}
          });
        } catch {}
      });

      const rtcConfig: RTCConfiguration = {};
      if (Array.isArray(iceServers) && iceServers.length) {
        rtcConfig.iceServers = iceServers as any;
      }
      if (iceTransportPolicy === 'relay') {
        (rtcConfig as any).iceTransportPolicy = 'relay';
      }
      await room.connect(sfu.url, sfu.token, { rtcConfig });
      lkRoomRef.current = room;

      const ms = micStreamRef.current;
      const mediaTrack = ms?.getAudioTracks?.()?.[0] || null;
      if (!mediaTrack) {
        throw new Error('Microphone stream is not available');
      }
      // Комментарий на русском: переиспользуем уже захваченный MediaStreamTrack, чтобы микрофон не "мигал".
      const constraints: MediaTrackConstraints = {
        deviceId: chosenMic ? { exact: chosenMic } : undefined,
        echoCancellation: !!s.echo_cancellation,
        noiseSuppression: !!s.noise_suppression,
        autoGainControl: !!s.auto_gain_control,
      };
      const LocalAudioTrack = (lk as any).LocalAudioTrack;
      const Track = (lk as any).Track;
      const localTrack = new LocalAudioTrack(mediaTrack, constraints, true);
      try {
        if (Track?.Source?.Microphone) localTrack.source = Track.Source.Microphone;
      } catch {}
      lkLocalAudioTrackRef.current = localTrack;

      const pub = await room.localParticipant.publishTrack(localTrack, {
        audioBitrate: AUDIO_BITRATE_BPS[q],
        source: (Track as any)?.Source?.Microphone,
        stopMicTrackOnMute: false,
      });
      lkAudioPubRef.current = pub;

      // Комментарий на русском: mute сразу применяем при реконнекте.
      try {
        if (isMutedRef.current) await pub?.mute?.();
      } catch {}

      // Комментарий на русском: анализатор громкости строим от MediaStreamTrack LiveKit.
      try {
        const stream = new MediaStream([localTrack.mediaStreamTrack]);
        startAnalyserFromStream(stream);
      } catch {}

      return true;
    } catch (e: any) {
      console.error('SFU connect failed:', e);
      const name = String(e?.name || '');
      if (name === 'NotAllowedError' || name === 'PermissionDeniedError') {
        lastSfuErrorRef.current = 'Нет доступа к микрофону (разрешение в браузере).';
      } else if (name === 'NotFoundError' || name === 'OverconstrainedError') {
        lastSfuErrorRef.current = 'Выбранный микрофон недоступен. Выберите другой.';
      } else {
        lastSfuErrorRef.current = 'Не удалось подключиться к голосовому серверу.';
      }
      await stopSfu();
      return false;
    }
  }, [startAnalyserFromStream, stopSfu]);

  // Начать захват микрофона
  const startLocalStream = useCallback(async (deviceId?: string) => {
    try {
      const s = voiceSettingsRef.current;
      const constraints: MediaStreamConstraints = {
        audio: {
          deviceId: deviceId ? { exact: deviceId } : undefined,
          echoCancellation: !!s.echo_cancellation,
          noiseSuppression: !!s.noise_suppression,
          autoGainControl: !!s.auto_gain_control,
        },
      };

      let stream: MediaStream | null = null;
      try {
        stream = await navigator.mediaDevices.getUserMedia(constraints);
      } catch (e: any) {
        // Комментарий на русском: если exact deviceId не подошёл — пробуем любой доступный.
        const name = String(e?.name || '');
        if (deviceId && (name === 'OverconstrainedError' || name === 'NotFoundError')) {
          stream = await navigator.mediaDevices.getUserMedia({
            audio: {
              echoCancellation: !!s.echo_cancellation,
              noiseSuppression: !!s.noise_suppression,
              autoGainControl: !!s.auto_gain_control,
            },
          });
        } else {
          throw e;
        }
      }
      startAnalyserFromStream(stream);

      return stream;
    } catch (e: any) {
      console.error('Failed to start local stream:', e);
      const name = String(e?.name || '');
      const msg = String(e?.message || '');
      let human = 'Не удалось получить доступ к микрофону';
      if (name === 'NotAllowedError' || name === 'SecurityError') human = 'Доступ к микрофону запрещён в браузере';
      else if (name === 'NotFoundError') human = 'Микрофон не найден';
      else if (name === 'NotSupportedError') human = 'Микрофон не поддерживается этим браузером';
      else if (msg.toLowerCase().includes('permissions policy')) human = 'Микрофон запрещён политикой сайта (Permissions-Policy)';
      onError?.(human);
      return null;
    }
  }, [onError, startAnalyserFromStream]);

  const acquireMicrophone = useCallback(async (deviceId?: string) => {
    // Комментарий на русском: захватываем микрофон один раз и переиспользуем трек (без “мигания” индикатора).
    try {
      const s = voiceSettingsRef.current;
      const mk = (did?: string): MediaStreamConstraints => ({
        audio: {
          deviceId: did ? { exact: did } : undefined,
          echoCancellation: !!s.echo_cancellation,
          noiseSuppression: !!s.noise_suppression,
          autoGainControl: !!s.auto_gain_control,
        },
      });

      const want = deviceId || null;
      const existing = micStreamRef.current;
      const existingDid = micDeviceIdRef.current;
      if (existing && existing.getAudioTracks().some(t => t.readyState === 'live') && existingDid === want) {
        return existing;
      }

      try {
        existing?.getTracks().forEach(t => t.stop());
      } catch {}
      micStreamRef.current = null;
      micDeviceIdRef.current = null;

      let stream: MediaStream | null = null;
      try {
        stream = await navigator.mediaDevices.getUserMedia(mk(deviceId));
      } catch (e: any) {
        const name = String(e?.name || '');
        if (deviceId && (name === 'OverconstrainedError' || name === 'NotFoundError')) {
          stream = await navigator.mediaDevices.getUserMedia(mk(undefined));
        } else {
          throw e;
        }
      }

      micStreamRef.current = stream;
      micDeviceIdRef.current = want;
      startAnalyserFromStream(stream); // индикатор уровня звука
      return stream;
    } catch (e: any) {
      const name = String(e?.name || '');
      const msg = String(e?.message || '');
      let human = 'Не удалось получить доступ к микрофону';
      if (name === 'NotAllowedError' || name === 'SecurityError') human = 'Доступ к микрофону запрещён в браузере';
      else if (name === 'NotFoundError') human = 'Микрофон не найден';
      else if (name === 'NotSupportedError') human = 'Микрофон не поддерживается этим браузером';
      else if (msg.toLowerCase().includes('permissions policy')) human = 'Микрофон запрещён политикой сайта (Permissions-Policy)';
      onError?.(human);
      return null;
    }
  }, [onError, startAnalyserFromStream]);

  const releaseMicrophone = useCallback(() => {
    try {
      micStreamRef.current?.getTracks().forEach(t => t.stop());
    } catch {}
    micStreamRef.current = null;
    micDeviceIdRef.current = null;
  }, []);

  // Остановить локальный стрим
  const stopLocalStream = useCallback(() => {
    if (animationFrameRef.current) {
      cancelAnimationFrame(animationFrameRef.current);
    }
    if (audioContextRef.current) {
      audioContextRef.current.close();
      audioContextRef.current = null;
    }
    if (streamRef.current) {
      streamRef.current.getTracks().forEach(t => t.stop());
      streamRef.current = null;
    }
    setLocalStream(null);
    setAudioLevel(0);
  }, []);

  useEffect(() => {
    stopLocalStreamRef.current = stopLocalStream;
  }, [stopLocalStream]);

  // Начать звонок
  const startCall = useCallback(async (quality?: VoiceQuality, e2ee = false) => {
    if (isConnecting) return;
    setIsConnecting(true);

    try {
      // Комментарий на русском: захватываем микрофон один раз по user-gesture и не отпускаем до выхода.
      const preMic = selectedMicId || voiceSettingsRef.current.preferred_mic_device_id || undefined;
      const preStream = await acquireMicrophone(preMic);
      if (!preStream) return;

      const mics = await loadMicrophones();
      let chosenMic = selectedMicId || voiceSettingsRef.current.preferred_mic_device_id || undefined;
      if (chosenMic && Array.isArray(mics) && mics.length && !mics.some(d => d.deviceId === chosenMic)) {
        // Комментарий на русском: сохранённый deviceId больше не существует — используем дефолтный.
        chosenMic = undefined;
        setSelectedMicId(null);
      }
      const q = computeEffectiveQuality(quality);
      setEffectiveQuality(q.effective);
      setQualityHint(q.hint);

      const response = await apiJson<{
        call_id: number;
        room_id: number;
        mode?: VoiceMode;
        sfu?: { url: string; room_name: string; token: string } | null;
        ice_servers?: IceServer[];
        ice_transport_policy?: 'all' | 'relay';
        e2ee_enabled: boolean;
        quality: VoiceQuality;
      }>(`/api/chat/rooms/${roomId}/voice-call/start/`, {
        method: 'POST',
        body: JSON.stringify({ e2ee, quality: q.effective }),
      });

      // Комментарий на русском: сначала обновляем состояние (участники), потом выбираем транспорт (SFU/P2P).
      const st = await fetchState();
      const participants = Array.isArray(st?.participants) ? st!.participants : [];
      const p2pTooMany = participants.length > P2P_MAX_PARTICIPANTS;

      let connected = false;
      if (response?.sfu && response?.mode !== 'p2p') {
        const sfuOk = await startSfu(response.sfu as SfuInfo, chosenMic, q.effective, response.ice_servers, response.ice_transport_policy);
        if (sfuOk) connected = true;
      }

      // Комментарий на русском: авто-fallback на P2P, если SFU недоступен/не подключился.
      if (!connected) {
        await stopSfu();
        if (p2pTooMany) {
          try {
            await apiJson(`/api/chat/rooms/${roomId}/voice-call/leave/`, { method: 'POST', body: JSON.stringify({ call_id: response.call_id }) });
          } catch {}
          onError?.(`P2P режим: лимит ${P2P_MAX_PARTICIPANTS} участников. Сейчас: ${participants.length}.`);
          releaseMicrophone();
          return;
        }
        const ok = await startP2p(response.call_id, response.ice_servers);
        if (!ok) {
          try {
            await apiJson(`/api/chat/rooms/${roomId}/voice-call/leave/`, { method: 'POST', body: JSON.stringify({ call_id: response.call_id }) });
          } catch {}
          onError?.(lastSfuErrorRef.current || 'Не удалось подключиться к голосовому чату.');
          releaseMicrophone();
          return;
        }
      }

      onCallStarted?.(response.call_id);
    } catch (e: any) {
      onError?.(e.message || 'Не удалось начать звонок');
      await stopSfu();
      await stopP2p();
      stopLocalStream();
      releaseMicrophone();
    } finally {
      setIsConnecting(false);
    }
  }, [roomId, isConnecting, selectedMicId, loadMicrophones, stopLocalStream, fetchState, onCallStarted, onError, apiJson, computeEffectiveQuality, startSfu, stopSfu, acquireMicrophone, releaseMicrophone, startP2p, stopP2p]);

  // Присоединиться к звонку
  const joinCall = useCallback(async (quality?: VoiceQuality) => {
    if (isConnecting) return;
    setIsConnecting(true);

    try {
      const preMic = selectedMicId || voiceSettingsRef.current.preferred_mic_device_id || undefined;
      const preStream = await acquireMicrophone(preMic);
      if (!preStream) return;

      const mics = await loadMicrophones();
      let chosenMic = selectedMicId || voiceSettingsRef.current.preferred_mic_device_id || undefined;
      if (chosenMic && Array.isArray(mics) && mics.length && !mics.some(d => d.deviceId === chosenMic)) {
        // Комментарий на русском: сохранённый deviceId больше не существует — используем дефолтный.
        chosenMic = undefined;
        setSelectedMicId(null);
      }
      const q = computeEffectiveQuality(quality);
      setEffectiveQuality(q.effective);
      setQualityHint(q.hint);

      const response = await apiJson<{
        call_id: number;
        room_id: number;
        mode?: VoiceMode;
        sfu?: { url: string; room_name: string; token: string } | null;
        ice_servers?: IceServer[];
        ice_transport_policy?: 'all' | 'relay';
        already_joined?: boolean;
        e2ee_enabled?: boolean;
        quality?: VoiceQuality;
      }>(`/api/chat/rooms/${roomId}/voice-call/join/`, {
        method: 'POST',
        body: JSON.stringify({ quality: q.effective }),
      });

      const st = await fetchState();
      const participants = Array.isArray(st?.participants) ? st!.participants : [];
      const p2pTooMany = participants.length > P2P_MAX_PARTICIPANTS;

      let connected = false;
      if (response?.sfu && response?.mode !== 'p2p') {
        const sfuOk = await startSfu(response.sfu as SfuInfo, chosenMic, q.effective, response.ice_servers, response.ice_transport_policy);
        if (sfuOk) connected = true;
      }

      if (!connected) {
        await stopSfu();
        if (p2pTooMany) {
          try {
            await apiJson(`/api/chat/rooms/${roomId}/voice-call/leave/`, { method: 'POST', body: JSON.stringify({ call_id: response.call_id }) });
          } catch {}
          onError?.(`P2P режим: лимит ${P2P_MAX_PARTICIPANTS} участников. Сейчас: ${participants.length}.`);
          releaseMicrophone();
          return;
        }
        const ok = await startP2p(response.call_id, response.ice_servers);
        if (!ok) {
          try {
            await apiJson(`/api/chat/rooms/${roomId}/voice-call/leave/`, { method: 'POST', body: JSON.stringify({ call_id: response.call_id }) });
          } catch {}
          onError?.(lastSfuErrorRef.current || 'Не удалось подключиться к голосовому чату.');
          releaseMicrophone();
          return;
        }
      }
    } catch (e: any) {
      onError?.(e.message || 'Не удалось присоединиться');
      await stopSfu();
      await stopP2p();
      stopLocalStream();
      releaseMicrophone();
    } finally {
      setIsConnecting(false);
    }
  }, [roomId, isConnecting, selectedMicId, loadMicrophones, stopLocalStream, fetchState, onError, apiJson, computeEffectiveQuality, startSfu, stopSfu, acquireMicrophone, releaseMicrophone, startP2p, stopP2p]);

  // Покинуть звонок
  const leaveCall = useCallback(async () => {
    try {
      if (callState.call_id) {
        await apiJson(`/api/chat/rooms/${roomId}/voice-call/leave/`, {
          method: 'POST',
          body: JSON.stringify({ call_id: callState.call_id }),
        });
      }
    } catch (e) {
      console.error('Failed to leave call:', e);
    } finally {
      await stopSfu();
      await stopP2p();
      stopLocalStream();
      releaseMicrophone();
      setCallState(prev => ({ ...prev, active: false, call_id: null, participants: [] }));
      setIsMuted(false);
      setIsDeafened(false);
      onCallEnded?.();
    }
  }, [roomId, callState.call_id, stopLocalStream, onCallEnded, apiJson, stopSfu, stopP2p, releaseMicrophone]);

  // Комментарий на русском: синхронизируем P2P peers по списку участников (mesh).
  useEffect(() => {
    if (!p2pActiveRef.current) return;
    if (!callState.active || !callState.call_id) return;
    const me = Number(currentUserId || 0) || 0;
    if (!me) return;
    const list = Array.isArray(callState.participants) ? callState.participants : [];
    const otherIds = list.map(p => Number(p?.user_id || 0)).filter(id => id && id !== me);
    // Комментарий на русском: в P2P не держим связи сверх лимита.
    const limited = otherIds.slice(0, Math.max(0, P2P_MAX_PARTICIPANTS - 1));
    limited.forEach((uid) => void p2pEnsureOfferToPeer(uid));
  }, [callState.active, callState.call_id, callState.participants, currentUserId, p2pEnsureOfferToPeer]);

  // Переключить mute
  const toggleMute = useCallback(async () => {
    const newMuted = !isMuted;
    setIsMuted(newMuted);

    // Отключаем/включаем аудио трек (fallback)
    if (streamRef.current) {
      streamRef.current.getAudioTracks().forEach(t => {
        t.enabled = !newMuted;
      });
    }

    // Комментарий на русском: если подключены к SFU — mute через публикацию.
    try {
      const pub = lkAudioPubRef.current;
      if (pub?.mute && pub?.unmute) {
        if (newMuted) await pub.mute();
        else await pub.unmute();
      } else if (lkLocalAudioTrackRef.current?.mute && lkLocalAudioTrackRef.current?.unmute) {
        if (newMuted) await lkLocalAudioTrackRef.current.mute();
        else await lkLocalAudioTrackRef.current.unmute();
      }
    } catch {}

    // Обновляем на сервере
    try {
      await apiJson(`/api/chat/rooms/${roomId}/voice-call/update-state/`, {
        method: 'POST',
        body: JSON.stringify({ muted_self: newMuted }),
      });
    } catch (e) {
      console.error('Failed to update mute state:', e);
    }
  }, [roomId, isMuted, apiJson]);

  // Переключить deafen
  const toggleDeafen = useCallback(async () => {
    const newDeafened = !isDeafened;
    setIsDeafened(newDeafened);

    // При deafen также mute
    if (newDeafened && !isMuted) {
      setIsMuted(true);
      if (streamRef.current) {
        streamRef.current.getAudioTracks().forEach(t => {
          t.enabled = false;
        });
      }
      try {
        const pub = lkAudioPubRef.current;
        if (pub?.mute) await pub.mute();
      } catch {}
    }

    try {
      await apiJson(`/api/chat/rooms/${roomId}/voice-call/update-state/`, {
        method: 'POST',
        body: JSON.stringify({ deafened_self: newDeafened, muted_self: newDeafened || isMuted }),
      });
    } catch (e) {
      console.error('Failed to update deafen state:', e);
    }
  }, [roomId, isDeafened, isMuted, apiJson]);

  // Сменить микрофон
  const changeMicrophone = useCallback(async (deviceId: string) => {
    setSelectedMicId(deviceId);
    void saveVoiceSettings({ preferred_mic_device_id: deviceId });
    if (lkLocalAudioTrackRef.current?.setDeviceId) {
      try {
        await lkLocalAudioTrackRef.current.setDeviceId(deviceId);
        return;
      } catch {}
    }
    if (localStream) {
      stopLocalStream();
      await startLocalStream(deviceId);
    }
  }, [localStream, stopLocalStream, startLocalStream, saveVoiceSettings]);

  const setSfuAudioBitrate = useCallback(async (q: Exclude<VoiceQuality, 'auto'>) => {
    // Комментарий на русском: LiveKit bitrate задаётся на publish options → делаем republish.
    const room = lkRoomRef.current;
    const track = lkLocalAudioTrackRef.current;
    if (!room || !track) return false;
    try {
      try { room.localParticipant.unpublishTrack(track); } catch {}
      const pub = await room.localParticipant.publishTrack(track, { audioBitrate: AUDIO_BITRATE_BPS[q] });
      lkAudioPubRef.current = pub;
      return true;
    } catch {
      return false;
    }
  }, []);

  const setPreferredQuality = useCallback(async (q: VoiceQuality) => {
    await saveVoiceSettings({ preferred_quality: q });
    if (callState.active) {
      const next = computeEffectiveQuality(q);
      setEffectiveQuality(next.effective);
      setQualityHint(next.hint);
      void setSfuAudioBitrate(next.effective);
      try {
        await apiJson(`/api/chat/rooms/${roomId}/voice-call/update-state/`, {
          method: 'POST',
          body: JSON.stringify({ quality: next.effective }),
        });
      } catch {
        // ignore
      }
    }
  }, [saveVoiceSettings, callState.active, computeEffectiveQuality, apiJson, roomId, setSfuAudioBitrate]);

  const setAudioProcessing = useCallback(async (patch: Pick<VoiceSettings, 'noise_suppression' | 'echo_cancellation' | 'auto_gain_control'>) => {
    const ok = await saveVoiceSettings(patch);
    if (!ok) return;
    if (lkLocalAudioTrackRef.current?.restartTrack) {
      try {
        const s = voiceSettingsRef.current;
        const chosenMic = selectedMicId || s.preferred_mic_device_id || undefined;
        await lkLocalAudioTrackRef.current.restartTrack({
          deviceId: chosenMic,
          echoCancellation: !!s.echo_cancellation,
          noiseSuppression: !!s.noise_suppression,
          autoGainControl: !!s.auto_gain_control,
        });
        return;
      } catch {}
    }
    if (localStream) {
      const mic = selectedMicId || voiceSettingsRef.current.preferred_mic_device_id || undefined;
      stopLocalStream();
      await startLocalStream(mic);
    }
  }, [saveVoiceSettings, localStream, selectedMicId, stopLocalStream, startLocalStream]);

  const setAutoQualityEnabled = useCallback(async (enabled: boolean) => {
    await saveVoiceSettings({ auto_quality_enabled: enabled });
  }, [saveVoiceSettings]);

  // Пригласить участника
  const inviteUser = useCallback(async (userId: number) => {
    try {
      await apiJson(`/api/chat/rooms/${roomId}/voice-call/invite/`, {
        method: 'POST',
        body: JSON.stringify({ user_id: userId }),
      });
      return true;
    } catch (e: any) {
      onError?.(e.message || 'Не удалось пригласить пользователя');
      return false;
    }
  }, [roomId, apiJson, onError]);

  // Комментарий на русском: SFU/WebRTC stats (берём RTCStatsReport от LiveKit sender/receiver).
  useEffect(() => {
    if (!callState.active) return;
    if (statsIntervalRef.current) clearInterval(statsIntervalRef.current);
    statsIntervalRef.current = setInterval(() => {
      (async () => {
        const t = lkLocalAudioTrackRef.current;
        if (!t?.getRTCStatsReport) return;
        const report: RTCStatsReport | undefined = await t.getRTCStatsReport();
        if (!report) return;

        let outbound: any = null;
        let remoteInbound: any = null;
        let pair: any = null;

        report.forEach((s: any) => {
          const isAudio = s?.kind === 'audio' || s?.mediaType === 'audio';
          if (s?.type === 'outbound-rtp' && isAudio) outbound = s;
          if (s?.type === 'remote-inbound-rtp' && isAudio) remoteInbound = s;
          if (s?.type === 'candidate-pair' && s?.state === 'succeeded' && (s?.nominated || s?.selected)) pair = s;
        });

        const now = Date.now();
        const prev = prevStatsRef.current;
        const sent = Number(outbound?.packetsSent ?? outbound?.packetsSent ?? 0) || 0;
        const lost = Number(remoteInbound?.packetsLost ?? 0) || 0;

        let lossRate: number | null = null;
        if (prev.ts && prev.packetsSent != null && prev.packetsLost != null) {
          const dSent = Math.max(0, sent - prev.packetsSent);
          const dLost = Math.max(0, lost - prev.packetsLost);
          const denom = dSent + dLost;
          if (denom > 0) lossRate = dLost / denom;
        }
        prevStatsRef.current = { packetsSent: sent, packetsLost: lost, ts: now };

        const rttMs = Number((pair?.currentRoundTripTime ?? remoteInbound?.roundTripTime) || 0) ? Number((pair?.currentRoundTripTime ?? remoteInbound?.roundTripTime) || 0) * 1000 : null;
        const jitterMs = Number(remoteInbound?.jitter || 0) ? Number(remoteInbound?.jitter || 0) * 1000 : null;
        const aob = Number(pair?.availableOutgoingBitrate || 0) ? Number(pair?.availableOutgoingBitrate || 0) / 1000 : null;

        const s0: WebrtcStats = {
          loss: lossRate,
          rttMs,
          jitterMs,
          availableOutgoingBitrateKbps: aob,
          hint: null,
        };
        s0.hint = formatStatsHint(s0);
        setWebrtcStats(s0);
      })().catch(() => {});
    }, 1500);

    return () => {
      if (statsIntervalRef.current) clearInterval(statsIntervalRef.current);
      statsIntervalRef.current = null;
    };
  }, [callState.active]);

  // Комментарий на русском: авто-качество в звонке (если есть WebRTC stats — используем их; иначе fallback по сети).
  useEffect(() => {
    if (!callState.active) return;
    if (qualityIntervalRef.current) clearInterval(qualityIntervalRef.current);

    const tick = async () => {
      const maxQ: VoiceQuality = voiceSettingsRef.current.preferred_quality;
      const autoEnabled = maxQ === 'auto' || !!voiceSettingsRef.current.auto_quality_enabled;
      if (!autoEnabled) {
        setEffectiveQuality(maxQ);
        setQualityHint(null);
        return;
      }

      const maxCap: Exclude<VoiceQuality, 'auto'> = (maxQ === 'auto' ? 'high' : maxQ);
      const s = webrtcStatsRef.current;
      let nextQ: Exclude<VoiceQuality, 'auto'> | null = null;
      let hint: string | null = null;

      if (s?.loss != null || s?.rttMs != null || s?.jitterMs != null) {
        const bad = (s.loss != null && s.loss > 0.05) || (s.rttMs != null && s.rttMs > 400) || (s.jitterMs != null && s.jitterMs > 30);
        if (bad) {
          autoBadCountRef.current += 1;
          autoGoodMsRef.current = 0;
        } else {
          autoBadCountRef.current = 0;
          autoGoodMsRef.current += 2500;
        }

        hint = s.hint;
        if (autoBadCountRef.current >= 3) {
          autoBadCountRef.current = 0;
          nextQ = clampQualityToMax(stepDownQuality(effectiveQualityRef.current), maxCap);
        } else if (autoGoodMsRef.current >= 20000) {
          autoGoodMsRef.current = 0;
          nextQ = clampQualityToMax(stepUpQuality(effectiveQualityRef.current), maxCap);
        } else {
          nextQ = clampQualityToMax(effectiveQualityRef.current, maxCap);
        }
      } else {
        const fallback = computeEffectiveQuality(maxQ);
        nextQ = fallback.effective;
        hint = fallback.hint;
      }

      setEffectiveQuality(nextQ);
      setQualityHint(hint);
      void setSfuAudioBitrate(nextQ);
      try {
        await apiJson(`/api/chat/rooms/${roomId}/voice-call/update-state/`, {
          method: 'POST',
          body: JSON.stringify({ quality: nextQ }),
        });
      } catch {
        // ignore
      }
    };

    void tick();
    qualityIntervalRef.current = setInterval(() => void tick(), 2500);
    return () => {
      if (qualityIntervalRef.current) clearInterval(qualityIntervalRef.current);
      qualityIntervalRef.current = null;
    };
  }, [callState.active, apiJson, roomId, computeEffectiveQuality, setSfuAudioBitrate]);

  // Polling состояния звонка
  useEffect(() => {
    if (roomId > 0) {
      fetchState();
      pollIntervalRef.current = setInterval(fetchState, 3000);
    }
    return () => {
      if (pollIntervalRef.current) {
        clearInterval(pollIntervalRef.current);
      }
    };
  }, [roomId, fetchState]);

  // Комментарий на русском: локально подсвечиваем "говорит" для себя (без SFU это единственный реальный сигнал).
  useEffect(() => {
    const uid = Number(currentUserId || 0) || 0;
    if (!uid) return;
    if (!callState.active) return;
    const speaking = !isMuted && audioLevel > 0.12;
    setCallState(prev => {
      const idx = prev.participants.findIndex(p => p.user_id === uid);
      if (idx < 0) return prev;
      const p = prev.participants[idx];
      if (!!p.speaking === speaking) return prev;
      const nextParticipants = prev.participants.slice();
      nextParticipants[idx] = { ...p, speaking };
      return { ...prev, participants: nextParticipants };
    });
  }, [currentUserId, callState.active, audioLevel, isMuted]);

  // Очистка при размонтировании
  useEffect(() => {
    return () => {
      void stopSfu();
      stopLocalStream();
      if (pollIntervalRef.current) {
        clearInterval(pollIntervalRef.current);
      }
      if (qualityIntervalRef.current) {
        clearInterval(qualityIntervalRef.current);
      }
      if (statsIntervalRef.current) {
        clearInterval(statsIntervalRef.current);
      }
    };
  }, [stopLocalStream, stopSfu]);

  // Комментарий на русском: загрузка персональных настроек голоса один раз.
  useEffect(() => {
    void loadVoiceSettings();
  }, [loadVoiceSettings]);

  return {
    // Состояние
    callState,
    isConnecting,
    isMuted,
    isDeafened,
    localStream,
    audioLevel,
    availableMics,
    selectedMicId,
    voiceSettings,
    effectiveQuality,
    qualityHint,
    webrtcStats,
    // Действия
    startCall,
    joinCall,
    leaveCall,
    toggleMute,
    toggleDeafen,
    changeMicrophone,
    inviteUser,
    loadMicrophones,
    refreshState: fetchState,
    setPreferredQuality,
    setAudioProcessing,
    setAutoQualityEnabled,
    reloadVoiceSettings: loadVoiceSettings,
  };
}
